import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radar, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function ClientLogin() {
  const [clientCode, setClientCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotCode, setForgotCode] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/client/login", { client_code: clientCode, password });
      login(data);
      navigate("/portal");
    } catch (err) {
      setError(formatApiError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const forgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const { data } = await api.post("/auth/client/forgot-password", { client_code: forgotCode });
      toast.success(data.message);
      if (data.dev_reset_token) {
        toast.info("Demo reset link ready", { description: "A password reset token was generated.", duration: 4000 });
        setForgotOpen(false);
        navigate(`/reset-password?token=${data.dev_reset_token}`);
      } else {
        setForgotOpen(false);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(29,78,216,0.14),transparent_55%)]" />
      <div className="w-full max-w-md relative">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 mb-8 transition-colors" data-testid="back-home">
          <ArrowLeft className="w-4 h-4" /> Back to website
        </Link>
        <div className="card-surface p-8 lg:p-10">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="flex items-center justify-center w-10 h-10 rounded-md bg-blue-600/20 border border-blue-500/30">
              <Radar className="w-5 h-5 text-cyan-400" />
            </span>
            <span className="font-display font-bold text-xl text-white">GEOTRAQ <span className="text-cyan-400">AI</span></span>
          </div>
          <h1 className="font-display text-2xl font-bold text-white mt-6">Client Login</h1>
          <p className="text-sm text-slate-400 mt-1">Access your campaign intelligence dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="client-login-form">
            <div>
              <Label className="text-slate-300 text-xs tracking-widest uppercase">Client Code</Label>
              <Input value={clientCode} onChange={(e) => setClientCode(e.target.value)} required placeholder="e.g. TRQ001"
                className="mt-1.5 bg-secondary/50 border-white/10 h-11" data-testid="client-code-input" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs tracking-widest uppercase">Password</Label>
              <div className="relative mt-1.5">
                <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••"
                  className="bg-secondary/50 border-white/10 h-11 pr-11" data-testid="client-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label="Toggle password visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cyan-400 transition-colors" data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded px-3 py-2" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 font-semibold h-11" data-testid="client-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "LOGIN TO DASHBOARD"}
            </Button>
            <button type="button" onClick={() => setForgotOpen(true)} className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors" data-testid="forgot-password-link">
              Forgot Password?
            </button>
          </form>
        </div>
        <p className="text-center text-xs text-slate-500 mt-6">Protected by secure authentication &amp; encrypted credentials.</p>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-popover border-white/10" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">Reset your password</DialogTitle>
            <DialogDescription className="text-slate-400">Enter your client code and we'll generate a password reset link.</DialogDescription>
          </DialogHeader>
          <form onSubmit={forgot} className="space-y-4 mt-2">
            <Input value={forgotCode} onChange={(e) => setForgotCode(e.target.value)} required placeholder="Client code"
              className="bg-secondary/50 border-white/10" data-testid="forgot-client-code" />
            <Button type="submit" disabled={forgotLoading} className="w-full bg-blue-600 hover:bg-blue-500" data-testid="forgot-submit">
              {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate Reset Link"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
